import React, { useState } from "react";
import { Tabs, Tab, Box, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import QuestionForm from "./QuestionForm";

type Props = {
  questions: any[];
  onChange: (newQuestions: any[]) => void;
  examPaper?: any;
  sectionSubject?: string;
  sectionSubjectName?: string;
};

function QuestionsTabs({ questions, onChange, examPaper, sectionSubject, sectionSubjectName }: Props) {
  const [tabIndex, setTabIndex] = useState(0);

  const addQuestion = () => {
    const newQ = {
      title: `Question ${questions.length + 1}`,
      qNo: "",
      type: "",
      marks: 1,
      sortOrder: questions.length + 1,
      subject: sectionSubject || "",
      subjectName: sectionSubjectName || "",
      chapter: "",
      topic: "",
      nodeType: "MainQuestion",
      choiceCount: 1,
    };
    const newList = [...questions, newQ];
    onChange(newList);
    setTabIndex(newList.length - 1);
  };

  const removeQuestion = (index: number) => {
    const newList = questions.filter((_, i) => i !== index);
    onChange(newList);
    if (tabIndex >= newList.length) {
      setTabIndex(Math.max(0, newList.length - 1));
    }
  };

  const updateQuestion = (index: number, newData: any) => {
    const newList = [...questions];
    newList[index] = newData;
    onChange(newList);
  };

  return (
    <Box>
      {/* Tabs Row */}
      <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
        {/* Scrollable Questions Tabs */}
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-flexContainer": {
              alignItems: "center",
              gap: 1,
            },
            "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "8px 8px 0 0",
              px: 3,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "200px",
              fontSize: "0.9rem",
              fontWeight: 500,
              color: "text.secondary",
              transition: "all 0.2s ease-in-out",
              backgroundColor: "grey.50",
              "&:hover": {
                backgroundColor: "grey.100",
                cursor: "pointer",
              },
            },
            "& .Mui-selected": {
              backgroundColor: "#fff",
              borderBottom: "1px solid white",
              fontWeight: 600,
              color: "primary.main",
              boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
            },
            "& .MuiTabs-scrollButtons": {
              color: "text.secondary",
              "&.Mui-disabled": { opacity: 0.3 },
            },
          }}
        >
          {questions.map((q, i) => (
            <Tab
              key={i}
              label={
                <Box display="flex" alignItems="center">
                  {q.title || `Question ${i + 1}`}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeQuestion(i);
                    }}
                    sx={{
                      ml: 1,
                      p: 0.5,
                      '&:hover': {
                        backgroundColor: 'error.light',
                        color: 'error.contrastText'
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            />
          ))}
        </Tabs>

        {/* Add Question (+) right after tabs, not scrollable */}
        <Tab
          icon={<AddIcon />}
          onClick={addQuestion}
          sx={{
            minHeight: 42,
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: "8px 8px 0 0",
            minWidth: 56,
            ml: 1,
            backgroundColor: "primary.light",
            color: "primary.contrastText",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "primary.main",
              transform: "scale(1.05)"
            }
          }}
        />
      </Box>

      {/* Active Question Form Container */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '0 2px 2px 2px',
          backgroundColor: '#fff',
          minHeight: 200
        }}
      >
        {questions.map((q, i) =>
          i === tabIndex ? (
            <QuestionForm
              key={i}
              index={i}
              data={q}
              onChange={(newData) => updateQuestion(i, newData)}
              examPaper={examPaper}
              sectionSubject={sectionSubject}
              sectionSubjectName={sectionSubjectName}
            />
          ) : null
        )}
      </Box>
    </Box>
  );
}

export default QuestionsTabs;
